import React from "react";
import { ThemeProvider } from "@emotion/react";
import { createTheme, Stack } from "@mui/material";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import Layout from "./Components/Layout";
import AdminDashboard from "./Pages/AdminDashboard";
import AdminHomeDashboard from "./Pages/AdminHomeDashboard";
import ForgetPswPage from "./Pages/ForgetPswPage";
import LoginPage from "./Pages/LoginPage";
import PartnerLogin from "./Pages/PartnerLogin";
import SignupPage from "./Pages/SignupPage";
import UpdateProfile from "./Pages/UpdatePage";
import UserDashboard from "./Pages/UserDashboard";
import ApiKeys from "./Pages/ApiKeys";
import { useGlobalAuthContext } from "./context/AuthContext";
import AddConnection from "./Pages/AddConnection";
import AddVibes from "./Pages/Vibes";
import ListOfIframes from "./Pages/ListOfIframes";
import LogsTable from "./Components/Logs/LogsTable";
import SingleLog from "./Components/Logs/SingleLog";
import { jwtDecode } from "jwt-decode";
import SketchUpload from "./Pages/sketch-upload";
import UploadFloor from "./Pages/UploadFloor";
import LayoutVersionSlider from "./Components/LayoutVersionSlider";
import Partners from "./Pages/Partners";
import Products from "./Pages/Products";
import ShopProducts from "./Pages/ShopProducts";
import Packages from "./Pages/Packages";
import FloorPlans from "./Pages/FloorPlans";
import LoaderSpinner from "./Components/LoaderSpinner";
import { getTenantBrandingColor } from "./utils/tenantConfig";
import ExternalAIUpload from "./Pages/ExternalAIUpload";
import IntegrationGuideDocs from "./Pages/IntegrationGuideDocs";
import SerhantStart from "./Pages/SerhantStart";
import UserMainHome from "./Pages/UserMainHome";
import Analytics from "./Pages/AnalyticsTwo";
import Cookies from "js-cookie";
import {
  getRoleFromAuthToken,
  isAllowedPathForNonAdminRoles,
  isMasterAdminJwtRole,
  isPartnerAdminJwtRole,
} from "./utils/routeAccess";
import {
  applyDocumentScrollPolicy,
  clearDocumentScrollPolicy,
} from "./utils/mobileViewportShell";
import { shouldDeferGuestEntryRoute } from "./utils/bootGate";
import {
  SPATIAL_BODY_CLASS,
  shouldApplySpatialVisualSystem,
} from "./theme/spatial";
import { shapePill } from "./theme/shape";

/**
 * Auth route-guard diagnostics. OFF by default so they don't flood the console
 * on every navigation (these logged ~10 lines per guard render). Re-enable
 * without a rebuild via any one of:
 *   - localStorage.setItem("mf:auth-debug", "1")  // persists across reloads
 *   - add ?mfAuthDebug=1 to the URL
 *   - set window.__MF_AUTH_DEBUG__ = true (then reload)
 */
const MF_AUTH_DEBUG_ENABLED = (() => {
  try {
    if (typeof window !== "undefined") {
      if (window.__MF_AUTH_DEBUG__ === true) return true;
      const ls = window.localStorage?.getItem("mf:auth-debug");
      if (ls === "1" || ls === "true") return true;
      if (/[?&]mfAuthDebug=1(?:&|$)/.test(window.location?.search || "")) return true;
    }
  } catch {
    /* ignore */
  }
  return false;
})();

/** Gated route-guard logger. No-op unless auth debugging is enabled. */
const authDbg = MF_AUTH_DEBUG_ENABLED
  ? (...args) => console.log(...args)
  : () => {};

/** Legacy `/log/:id` → `/logs/:id` (bookmarks and shared links). */
function LegacyLogDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/logs/${id}`} replace />;
}

/** Same as LegacyLogDetailRedirect for `/admin/log/:id` embed tree. */
function AdminLegacyLogDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/admin/logs/${id}`} replace />;
}

/** Full-viewport block when a non-admin hits a master-admin-only route (no sidebar chrome). */
function MasterAdminAccessDenied() {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        background: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: 0,
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 14,
        color: "rgba(0,0,0,0.65)",
      }}
    >
      This page could not be accessed.
    </div>
  );
}

/**
 * Avoid a one-frame "access denied" flash post-login when JWT role isn't readable yet
 * (context/cookie timing). Known roles deny immediately; unknown shows a white loader briefly.
 */
function DeferredMasterAdminAccessDenied({ authToken, pathname }) {
  const role = getRoleFromAuthToken(authToken);
  const [showDeny, setShowDeny] = React.useState(() => role != null);

  React.useEffect(() => {
    if (role != null) {
      setShowDeny(true);
      return undefined;
    }
    const t = window.setTimeout(() => setShowDeny(true), 180);
    return () => window.clearTimeout(t);
  }, [role, authToken, pathname]);

  if (!showDeny) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        height="100dvh"
        sx={{ bgcolor: "#fff" }}
      >
        <LoaderSpinner text={false} />
      </Stack>
    );
  }
  return <MasterAdminAccessDenied />;
}

const App = () => {
  const { currentUserSessionStorage, brandingColor  , allowGuestUpload   , loaderDomain} = useGlobalAuthContext();

  const location = useLocation();

  // Get tenant-specific branding color, fallback to context branding color
  const tenantBrandingColor = getTenantBrandingColor();
  const effectiveBrandingColor = tenantBrandingColor !== '#1B1B1B' ? tenantBrandingColor : brandingColor;

  // Sync brand color to CSS custom property so index.css can use var(--brand-color)
  React.useEffect(() => {
    const color = effectiveBrandingColor || "#006766";
    document.documentElement.style.setProperty("--brand-color", color);
    const hex = color.replace("#", "");
    if (hex.length === 6) {
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      document.documentElement.style.setProperty("--brand-color-rgb", `${r}, ${g}, ${b}`);
    }
  }, [effectiveBrandingColor]);

  // Spatial visual system rollback toggle:
  // body.mf-spatial scopes the consolidated style overrides in index.css to
  // routes where the floor plan is the primary content surface (editor +
  // gallery + admin viewers). Build-time env var
  // REACT_APP_ENABLE_SPATIAL_VISUAL_SYSTEM=false disables this scope.
  React.useEffect(() => {
    const enable = shouldApplySpatialVisualSystem(location.pathname);
    document.body.classList.toggle(SPATIAL_BODY_CLASS, enable);
    return () => {
      document.body.classList.remove(SPATIAL_BODY_CLASS);
    };
  }, [location.pathname]);

  React.useEffect(() => {
    // /docs and /start are standalone pages. The global body scroll policy sets
    // an inline overflow on <body>, which turns it into a (non-scrolling) scroll
    // container and clips content that is taller than the viewport.
    if (location.pathname === "/docs" || location.pathname === "/start") {
      clearDocumentScrollPolicy();
      return undefined;
    }

    const isAdminLikeRoute =
      (currentUserSessionStorage &&
        jwtDecode(currentUserSessionStorage)?.user?.role === "admin") ||
      location.pathname === "/edit" ||
      location.pathname === "/admin" ||
      location.pathname.startsWith("/admin/");

    const updateBodyStyles = () => {
      applyDocumentScrollPolicy({ isAdminLikeRoute });
    };

    updateBodyStyles();

    const observer = new MutationObserver(() => {
      updateBodyStyles();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const handleResize = () => {
      updateBodyStyles();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
      clearDocumentScrollPolicy();
    };
  }, [currentUserSessionStorage, location.pathname]);

  const lightenDarkenColor = (hex, percent) => {
    let num = parseInt(hex.slice(1), 16),
      amt = Math.round(2.55 * percent),
      r = (num >> 16) + amt,
      g = ((num >> 8) & 0x00ff) + amt,
      b = (num & 0x0000ff) + amt;

    return (
      `#${(0 | ((1 << 8) + (r < 255 ? (r < 1 ? 0 : r) : 255)))
        .toString(16)
        .slice(1)}` +
      `${(0 | ((1 << 8) + (g < 255 ? (g < 1 ? 0 : g) : 255)))
        .toString(16)
        .slice(1)}` +
      `${(0 | ((1 << 8) + (b < 255 ? (b < 1 ? 0 : b) : 255)))
        .toString(16)
        .slice(1)}`
    );
  };

  const generateGradients = (baseColor) => {
    const darkShade = lightenDarkenColor(baseColor, -40); 
    const lightShade = lightenDarkenColor(baseColor, 40); 

    return {
      primary: `linear-gradient(90deg, ${darkShade}, ${lightShade})`,
      primary1: `linear-gradient(180deg, ${darkShade}, ${lightShade})`,
      primary2: `linear-gradient(270deg, ${darkShade}, ${lightShade})`,
    };
  };

  const theme = createTheme({
    typography: {
      fontFamily: ["Inter"].join(","),
    },
    palette: {
      primary: {
        main: effectiveBrandingColor || "#202124",
      },
      customPrimary: {
        main: effectiveBrandingColor || "#202124",
      },
      /** iOS-style destructive red — used for danger rows/buttons across menus and modals. */
      error: {
        main: "#FF3B30",
      },
    },
    gradients: generateGradients(effectiveBrandingColor || "#202124"),
    customShadows: [
      "none",
      `0 0 0 2px ${effectiveBrandingColor || "#202124"} !important`, 
    ],
    customBorders: ["none", `2px dashed ${effectiveBrandingColor || "#202124"}`],
    customOutlines: [
      "none",
      `2px solid ${effectiveBrandingColor || "rgba(0, 103, 102, 1)"} !important`, 
    ],
    components: {
      /**
       * Baseline for every MUI Button / LoadingButton that doesn't opt into
       * a pill helper (getMFPrimaryButtonSx etc.) or the legacy .button-style
       * CSS class. Restores the pill shape + sentence casing the old global
       * `button { border-radius: 50px; text-transform: capitalize }` rule
       * provided before it was scoped off MUI buttons. Helpers (which set
       * borderRadius/textTransform with !important) and .button-style (CSS
       * !important) still override this default where they apply.
       */
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: shapePill,
            textTransform: "none",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          filledInfo: {
            backgroundColor: "rgba(255, 171, 0, 0.16)",
            color: "rgb(183, 110, 0)",
            padding: "0px 12px",
            fontSize: "0.75rem",
            fontWeight: 700,
            height: "24px",
            minWidth: "24px",
            borderRadius: "6px",
            "& svg": {
              fontSize: "0.875rem",
            },
          },
          filledSuccess: {
            backgroundColor: "#BCE3C566",
            color: "rgb(17, 141, 87) ",
            padding: "4px 4px",
            fontSize: "9px",
            height: "auto",
            fontWeight: 700,
            "& .MuiChip-label": {
              padding: 0,
              paddingLeft: "4px",
              paddingRight: "4px",
            },
            "& .MuiChip-icon": {
              marginLeft: "4px",
              marginRight: "4px",
            },
            borderRadius: "4px",
          },
          filledError: {
            backgroundColor: "rgba(255, 86, 48, 0.16)",
            color: "rgb(183, 29, 24)",
            fontSize: "0.75rem",
            fontWeight: 700,
            padding: "0px 12px",
            height: "24px",
            minWidth: "24px",
            borderRadius: "6px",
            "& svg": {
              fontSize: "0.875rem",
            },
          },
          filledWarning: {
            backgroundColor: "rgba(145, 158, 171, 0.16)",
            color: "rgb(99, 115, 129)",
            fontSize: "0.75rem",
            padding: "0px 12px",
            fontWeight: 700,
            height: "24px",
            minWidth: "24px",
            borderRadius: "6px",
            "& svg": {
              fontSize: "0.875rem",
            },
          },
          filledSecondary: {
            backgroundColor: "rgba(0, 184, 217, 0.16)",
            color: "rgb(0, 108, 156)",
            padding: "0px 12px",
            fontSize: "0.75rem",
            fontWeight: 700,
            height: "24px",
            minWidth: "24px",
            borderRadius: "6px",
            "& svg": {
              fontSize: "0.875rem",
            },
          },
        },
      },
    },
  });

  const RequireElevatedPrivs = ({ children }) => {
    // =====================================================
    // FIX: Check BOTH context state AND cookie
    // =====================================================
    // The cookie is set synchronously, but context state updates async.
    // After login/signup, the cookie exists immediately but context
    // might not have updated yet. Check both to avoid race conditions.
    // =====================================================
    const cookieToken = Cookies.get("token");
    const isAuthenticated = currentUserSessionStorage || cookieToken;
    // Try to get token from either source
    let authToken = currentUserSessionStorage;
    if (!authToken && cookieToken) {
      try {
        authToken = JSON.parse(cookieToken);
      } catch (e) {
        authToken = cookieToken;
      }
    }
    if (
      authToken &&
      (isMasterAdminJwtRole(getRoleFromAuthToken(authToken)) || isPartnerAdminJwtRole(getRoleFromAuthToken(authToken)))
    ) {
      console.log('[RequireElevatedPrivs] ✅ Authenticated with elevated privileges, allowing route:', location.pathname);
      return children;
    } else if (!isAuthenticated) {
      sessionStorage.setItem(
        "redirectPath",
        `${location.pathname}${location.search}`
      );
      console.log('[RequireElevatedPrivs] → Not authenticated, redirecting to /login');
      return <Navigate to="/login" />;
    }
    console.log(
      "[RequireElevatedPrivs] → Non-admin blocked from elevated privileges route:",
      location.pathname
    );
    return (
      <DeferredMasterAdminAccessDenied
        authToken={authToken}
        pathname={location.pathname}
      />
    );
  }

  const RequireAuth = ({ children }) => {
    // =====================================================
    // FIX: Check BOTH context state AND cookie
    // =====================================================
    // The cookie is set synchronously, but context state updates async.
    // After login/signup, the cookie exists immediately but context
    // might not have updated yet. Check both to avoid race conditions.
    // =====================================================
    const cookieToken = Cookies.get("token");
    const isAuthenticated = currentUserSessionStorage || cookieToken;

    // Try to get token from either source
    let authToken = currentUserSessionStorage;
    if (!authToken && cookieToken) {
      try {
        authToken = JSON.parse(cookieToken);
      } catch (e) {
        authToken = cookieToken;
      }
    }

    // Detailed logging for debugging auth flow (gated; see authDbg)
    authDbg('');
    authDbg('╔' + '═'.repeat(60) + '╗');
    authDbg('║  🔒 [RequireAuth] Route Guard Check                         ║');
    authDbg('╠' + '═'.repeat(60) + '╣');
    authDbg('║  Route:', location.pathname);
    authDbg('║  Context token:', !!currentUserSessionStorage);
    authDbg('║  Cookie token:', !!cookieToken);
    authDbg('║  isAuthenticated:', !!isAuthenticated);
    authDbg('║  sessionStorage.redirectPath:', sessionStorage.getItem("redirectPath") || '(not set)');
    authDbg('╚' + '═'.repeat(60) + '╝');
    
    if (
      shouldDeferGuestEntryRoute({
        loaderDomain,
        isAuthenticated,
        pathname: location.pathname,
      })
    ) {
      // Branding never blocks the app. The only first-paint decision that needs
      // the (async) partner flags is the guest redirect on the two entry routes;
      // defer just those until the flags resolve (bounded by the watchdog in
      // AuthContext). Every other route renders immediately. See bootGate.js.
      authDbg('[RequireAuth] → Deferring guest entry-route redirect (partner flags loading)');
      return <Stack justifyContent={"center"} height={"100dvh"}>
        <LoaderSpinner text={false}/>
        </Stack>;
    }
    else if (!isAuthenticated && location.pathname.startsWith("/gallery/")) {
      const id = location.pathname.split("/gallery/")[1];
      sessionStorage.setItem("redirectPath", `/floor-gallery/${id}`);
      authDbg('[RequireAuth] → Gallery route, allowing (not auth required)');
      return children;
    } else if (!isAuthenticated && location.pathname.startsWith("/admin/gallery/")) {
      const id = location.pathname.split("/admin/gallery/")[1];
      sessionStorage.setItem("redirectPath", `/admin/floor-gallery/${id}`);
      authDbg('[RequireAuth] → Admin embed gallery route, allowing (not auth required)');
      return children;
    } else if (!isAuthenticated && location.pathname.startsWith("/floor-gallery/")) {
      const id = location.pathname.split("/floor-gallery/")[1];
      sessionStorage.setItem("redirectPath", `/floor-gallery/${id}`);
      authDbg('[RequireAuth] → Floor-gallery route, allowing guest editor');
      return children;
    } else if (!isAuthenticated && location.pathname.startsWith("/admin/floor-gallery/")) {
      const id = location.pathname.split("/admin/floor-gallery/")[1];
      sessionStorage.setItem("redirectPath", `/admin/floor-gallery/${id}`);
      authDbg('[RequireAuth] → Admin embed floor-gallery route, allowing guest editor');
      return children;
    }
    else if(!isAuthenticated && location.pathname !== "/main" && location.pathname !== "/") {
      sessionStorage.setItem("redirectPath", location.pathname);
      authDbg('[RequireAuth] ⚠️ NOT AUTHENTICATED! Redirecting to /login');
      authDbg('[RequireAuth] ⚠️ This should NOT happen after signup if cookie check works');
      return <Navigate to="/login" />;
    }
    else if (
      authToken &&
      isMasterAdminJwtRole(getRoleFromAuthToken(authToken)) &&
      location.pathname === "/main"
    ) {
      authDbg('[RequireAuth] → Admin at /main, redirecting to /');
      return <Navigate to="/" />;
    } else if (
      authToken &&
      isMasterAdminJwtRole(getRoleFromAuthToken(authToken))
    ) {
      authDbg('[RequireAuth] → Admin, allowing route');
      return children;
    } else if (
      authToken &&
      location.pathname === "/" &&
      !isMasterAdminJwtRole(getRoleFromAuthToken(authToken))
    ) {
      // Must run before master-admin deny: `/` is not in the non-admin allowlist, but end users
      // should never land on the admin index (Partners); send them to /main.
      authDbg('[RequireAuth] → Non-admin at /, redirecting to /main');
      return <Navigate to="/main" replace />;
    } else if (
      authToken &&
      !isMasterAdminJwtRole(getRoleFromAuthToken(authToken)) &&
      !isAllowedPathForNonAdminRoles(location.pathname)
    ) {
      authDbg(
        "[RequireAuth] → Non-admin blocked from master-admin route:",
        location.pathname
      );
      return (
        <DeferredMasterAdminAccessDenied
          authToken={authToken}
          pathname={location.pathname}
        />
      );
    } else if (allowGuestUpload && !authToken && location.pathname === "/") {
      authDbg('[RequireAuth] → Guest upload at /, redirecting to /main');
      return <Navigate to="/main" replace />;
    } else if (!isAuthenticated && !allowGuestUpload) {
      sessionStorage.setItem("redirectPath", location.pathname);
      authDbg('[RequireAuth] ⚠️ NOT AUTHENTICATED (fallback)! Redirecting to /signup');
      return <Navigate to="/signup" />;
    } else {
      authDbg('[RequireAuth] ✅ Authenticated, allowing route:', location.pathname);
      return children;
    }
  };


  // =====================================================
  // RedirectAuth - ROUTE GUARD ONLY (per Peter's recommendation)
  // =====================================================
  // This component ONLY gates auth-required routes.
  // It does NOT redirect logged-in users away from routes.
  //
  // Rules:
  // - Not logged in? Show children (login/signup page)
  // - Logged in admin? Redirect to /
  // - Logged in user? Allow route (return children)
  //
  // CRITICAL: Do NOT redirect logged-in users to /main here!
  // That would break: deep links, page refresh, shared links.
  // =====================================================
  const RedirectAuth = ({ children }) => {
    // Check BOTH context state AND cookie (cookie is sync, context is async)
    const cookieToken = Cookies.get("token");
    const isAuthenticated = currentUserSessionStorage || cookieToken;
    
    let authToken = currentUserSessionStorage;
    if (!authToken && cookieToken) {
      try {
        authToken = JSON.parse(cookieToken);
      } catch (e) {
        authToken = cookieToken;
      }
    }
    
    // Detailed logging for debugging auth flow
    console.log('[RedirectAuth] Checking route:', location.pathname);
    console.log('[RedirectAuth] Context token:', !!currentUserSessionStorage);
    console.log('[RedirectAuth] Cookie token:', !!cookieToken);
    console.log('[RedirectAuth] isAuthenticated:', !!isAuthenticated);
    
    if (!isAuthenticated) {
      // Not logged in - show the auth page (login/signup)
      console.log('[RedirectAuth] → Not authenticated, showing auth page');
      return children;
    } else if (
      authToken &&
      jwtDecode(authToken)?.user?.role === "admin"
    ) {
      // Admin - redirect away from user auth pages
      console.log('[RedirectAuth] → Admin, redirecting to /');
      return <Navigate to="/" />;
    } else {
      // Logged in user - allow the route to proceed
      // Login/Signup handlers will navigate to correct destination
      console.log('[RedirectAuth] ✅ User authenticated, allowing auth page (handler will navigate)');
      return children;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Routes>
        <Route path="/docs" element={<IntegrationGuideDocs />} />
        <Route path="/start" element={<SerhantStart />} />
        <Route path="/" exact element={<Layout />}>
         
          <Route
            path="/analytics"
            element={
              <RequireElevatedPrivs>
                <Analytics />
              </RequireElevatedPrivs>
            }
          />
          <Route
            path="/main"
            element={
              <RequireAuth>
                <UserMainHome />
              </RequireAuth>
            }
          />

          <Route
            path="/floor-plan/:layoutId/version/:version"
            element={
              <RequireAuth>
                <SketchUpload />
              </RequireAuth>
            }
          />

          <Route
            path="/floor-gallery/:galleryId"
            element={
              <RequireAuth>
                <SketchUpload />
              </RequireAuth>
            }
          />

          <Route
            path="/upload-floor"
            element={
              <RequireAuth>
                <UploadFloor />
              </RequireAuth>
            }
          />

          <Route
            path="/layout/:id"
            element={
              <RequireAuth>
                <UploadFloor />
              </RequireAuth>
            }
          />

          <Route
            path="/gallery/:id"
            element={
              <RequireAuth>
                <LayoutVersionSlider />
              </RequireAuth>
            }
          />

          <Route
            path="/layouts"
            element={
              <RequireAuth>
                <AdminDashboard />
              </RequireAuth>
            }
          />

          <Route
            path="/floorPlans"
            element={
              <RequireAuth>
                <FloorPlans />
              </RequireAuth>
            }
          />

          {/* External AI Upload - Public route for integration mode */}
          <Route
            path="/external-ai-upload"
            element={<ExternalAIUpload />}
          />

          <Route path="/partner-login" exact element={<PartnerLogin />} />

          <Route
            path="/login"
            exact
            element={
              <RedirectAuth>
                <LoginPage />{" "}
              </RedirectAuth>
            }
          />
          <Route
            path="/signup"
            exact
            element={
              <RedirectAuth>
                <SignupPage />
              </RedirectAuth>
            }
          />

          <Route path="/edit" exact element={<UpdateProfile />} />
          <Route path="/forgetPsw" exact element={<ForgetPswPage />} />

          <Route
            path="/users"
            element={
              <RequireAuth>
                <UserDashboard />
              </RequireAuth>
            }
          />
          <Route path="/user" element={<Navigate to="/users" replace />} />
          <Route
            path="/api-keys"
            element={
              <RequireAuth>
                <ApiKeys />
              </RequireAuth>
            }
          />

          {/* Default admin landing */}
          <Route
            index
            element={
              <RequireAuth>
                <Partners />
              </RequireAuth>
            }
          />

          <Route
            path="/partners"
            element={
              <RequireAuth>
                <Partners />
              </RequireAuth>
            }
          />

          {/* Admin Home — upload activity dashboard (live once backend implements
              GET /external-upload/activity; mock data shown in development) */}
          <Route
            path="/home"
            element={
              <RequireAuth>
                <AdminHomeDashboard />
              </RequireAuth>
            }
          />

          <Route
            path="/products"
            element={
              <RequireAuth>
                <Products />
              </RequireAuth>
            }
          />

          <Route
            path="/affiliate-products"
            element={
              <RequireAuth>
                <ShopProducts />
              </RequireAuth>
            }
          />
          {/* Legacy slug — redirects so old links and bookmarks keep working */}
          <Route path="/shop-products" element={<Navigate to="/affiliate-products" replace />} />

          <Route
            path="/packages"
            element={
              <RequireAuth>
                <Packages />
              </RequireAuth>
            }
          />

          <Route
            path="/connectionPoints"
            element={
              <RequireAuth>
                <AddConnection />
              </RequireAuth>
            }
          />
          <Route
            path="/vibes"
            element={
              <RequireAuth>
                <AddVibes />
              </RequireAuth>
            }
          />
          <Route
            path="/amazonlist"
            element={
              <RequireAuth>
                <ListOfIframes />
              </RequireAuth>
            }
          />
          <Route
            path="/logs"
            element={
              <RequireAuth>
                <LogsTable />
              </RequireAuth>
            }
          />
          <Route
            path="/logs/:id"
            element={
              <RequireAuth>
                <SingleLog />
              </RequireAuth>
            }
          />
          <Route path="/log" element={<Navigate to="/logs" replace />} />
          <Route path="/log/:id" element={<LegacyLogDetailRedirect />} />

          {/* Admin embed — same screens as above, under /admin for client iframes */}
          <Route
            path="/admin"
            element={<Navigate to="/admin/partners" replace />}
          />

          <Route
            path="/admin/floor-plan/:layoutId/version/:version"
            element={
              <RequireAuth>
                <SketchUpload />
              </RequireAuth>
            }
          />

          <Route
            path="/admin/floor-gallery/:galleryId"
            element={
              <RequireAuth>
                <SketchUpload />
              </RequireAuth>
            }
          />

          <Route
            path="/admin/upload-floor"
            element={
              <RequireAuth>
                <UploadFloor />
              </RequireAuth>
            }
          />

          <Route
            path="/admin/layout/:id"
            element={
              <RequireAuth>
                <UploadFloor />
              </RequireAuth>
            }
          />

          <Route
            path="/admin/gallery/:id"
            element={
              <RequireAuth>
                <LayoutVersionSlider />
              </RequireAuth>
            }
          />

          <Route
            path="/admin/layouts"
            element={
              <RequireAuth>
                <AdminDashboard />
              </RequireAuth>
            }
          />

          <Route
            path="/admin/floorPlans"
            element={
              <RequireAuth>
                <FloorPlans />
              </RequireAuth>
            }
          />

          <Route
            path="/admin/external-ai-upload"
            element={<ExternalAIUpload />}
          />

          <Route
            path="/admin/users"
            element={
              <RequireAuth>
                <UserDashboard />
              </RequireAuth>
            }
          />
          <Route path="/admin/user" element={<Navigate to="/admin/users" replace />} />
          <Route
            path="/admin/api-keys"
            element={
              <RequireAuth>
                <ApiKeys />
              </RequireAuth>
            }
          />

          <Route
            path="/admin/partners"
            element={
              <RequireAuth>
                <Partners />
              </RequireAuth>
            }
          />

          <Route
            path="/admin/home"
            element={
              <RequireAuth>
                <AdminHomeDashboard />
              </RequireAuth>
            }
          />

          <Route
            path="/admin/products"
            element={
              <RequireAuth>
                <Products />
              </RequireAuth>
            }
          />

          <Route
            path="/admin/affiliate-products"
            element={
              <RequireAuth>
                <ShopProducts />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/shop-products"
            element={<Navigate to="/admin/affiliate-products" replace />}
          />

          <Route
            path="/admin/packages"
            element={
              <RequireAuth>
                <Packages />
              </RequireAuth>
            }
          />

          <Route
            path="/admin/connectionPoints"
            element={
              <RequireAuth>
                <AddConnection />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/vibes"
            element={
              <RequireAuth>
                <AddVibes />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/amazonlist"
            element={
              <RequireAuth>
                <ListOfIframes />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/logs"
            element={
              <RequireAuth>
                <LogsTable />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/logs/:id"
            element={
              <RequireAuth>
                <SingleLog />
              </RequireAuth>
            }
          />
          <Route path="/admin/log" element={<Navigate to="/admin/logs" replace />} />
          <Route path="/admin/log/:id" element={<AdminLegacyLogDetailRedirect />} />
        </Route>
      </Routes>
    </ThemeProvider>
  );
};

export default App;
